import type { OutletSummary } from "@/app/services/generated/outletService";
import type { SupplierManufacturer } from "@/app/services/generated/suppliermanufacturerService";
import type { Group } from "@/app/services/generated/groupService";
import type { PurchaseOrderHeader } from "@/app/services/generated/purchaseorderService";
import {
  PurchaseOrderFilter,
  purchaseOrderService,
} from "@/app/services/generated/purchaseorderService";
import type { StockTakeHeader } from "@/app/services/generated/stocktakeService";
import {
  StockTakeFilter,
  stockTakeService,
} from "@/app/services/generated/stocktakeService";
import type { UllageHeader } from "@/app/services/generated/ullageService";
import {
  UllageFilter,
  ullageService,
} from "@/app/services/generated/ullageService";
import type { PriceList } from "@/app/services/generated/pricelistService";
import type {
  AssemblyProcessLine,
  ProcessLineForAssembly,
  AssemblyHeader,
} from "@/app/services/generated/assemblyService";
import {
  AssemblyFilter,
  assemblyService,
} from "@/app/services/generated/assemblyService";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import type { CreditSummary } from "@/app/services/generated/creditService";
import {
  CreditFilter,
  creditService,
} from "@/app/services/generated/creditService";
import { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import type {
  OutletLastOrderTimestamp,
  OrderSummary,
} from "@/app/services/generated/orderService";
import { OrderFilter } from "@/app/services/generated/orderService";
import type Guid from "@/app/types/common/guid";
import { GuidMap, GuidSet } from "@/app/types/common/guidcollections";
import { contactsMenu } from "@/app/ui/navigation/contactsMenu";
import { priceListSubRoutes } from "@/app/ui/navigation/pricingMenu";
import { productionMenu } from "@/app/ui/navigation/productionMenu";
import { salesMenu } from "@/app/ui/navigation/salesMenu";
import { stockMenu } from "@/app/ui/navigation/stockMenu";
import { ullageRoutes } from "@/app/ui/navigation/ullagesMenu";
import { translateWithParams, useI18n } from "@/plugins/i18n";
import type { Location } from "vue-router";
import { orderService } from "@/app/services/extensions/orderServiceExtended";
import type { Lead } from "@/app/services/generated/leadService";
import { leadService } from "@/app/services/generated/leadService";
import apiCache from "@/app/caching/apiCache";
import type OutletSummariesCache from "@/app/caching/caches/outletSummariesCache";
import type SupplierManufacturersCache from "@/app/caching/caches/supplierManufacturersCache";
import type PriceListsCache from "@/app/caching/caches/priceListsCache";
import { distinctGuids, isNotNullOrUndefined, toGuidMap } from "@/app/apiutils";
import type { Vessel } from "@/app/services/generated/vesselv2Service";
import type { PalletHeader } from "@/app/services/generated/palletService";
import {
  PalletFilter,
  palletService,
} from "@/app/services/generated/palletService";
import { distributionMenu } from "@/app/ui/navigation/distributionMenu";
import { EnumPalletStatus } from "@/app/services/generated/enums/enumPalletStatus.generated";
import type { StockTransferHeader } from "@/app/services/generated/stocktransferService";
import {
  StockTransferFilter,
  stockTransferService,
} from "@/app/services/generated/stocktransferService";
import type { StockAdjustmentHeader } from "@/app/services/generated/stockadjustmentService";
import {
  StockAdjustmentFilter,
  stockAdjustmentService,
} from "@/app/services/generated/stockadjustmentService";
import type VesselsCache from "@/app/caching/caches/vesselsCache";
import {
  batchService,
  BatchFilter,
  ProcessInstructionFilter,
} from "@/app/services/generated/batchService";
import type {
  ProcessHeader,
  ProcessInstruction,
  BatchHeader,
} from "@/app/services/generated/batchService";
import { batchMenu } from "@/app/ui/navigation/batchMenu";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { hasFeatureUsingUserStore } from "@/app/featureFlags";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import GroupsCache from "@/app/caching/caches/groupsCache";
import ItemsCache from "@/app/caching/caches/itemsCache";
import type { Item } from "@/app/services/generated/itemService";
import { EnumStockTakeStatus } from "@/app/services/generated/enums/enumStockTakeStatus.generated";
import userStore from "@/store/modules/userStore";
import { buildCache } from "@/app/caching/buildCache";
import { EnumAssemblyStatus } from "@/app/services/generated/enums/enumAssemblyStatus.generated";

const { $m } = useI18n();

interface ProcessLineWithAssembly {
  processLine: AssemblyProcessLine;
  processLineId: Guid;
  assemblyHeader: AssemblyHeader;
}

interface ProcessInstructionWithParentDetails {
  instruction: ProcessInstruction;
  process: ProcessHeader;
  batch: BatchHeader | null;
}

export interface TaskWithAssociations {
  task: BmTask;
  associationName: string | null;
  associationRoute: Location | null;

  // association - at most one of these objects will be populated
  associatedOutlet?: OutletSummary;
  associatedOutletLastOrder?: Readonly<OutletLastOrderTimestamp>;
  associatedOutletGroup?: Readonly<Group>;
  associatedOrder?: Readonly<OrderSummary>;
  associatedAssembly?: Readonly<AssemblyHeader>;
  associatedAssemblyProcessLine?: Readonly<ProcessLineWithAssembly>;
  associatedCredit?: Readonly<CreditSummary>;
  associatedLead?: Readonly<Lead>;
  associatedPriceList?: Readonly<PriceList>;
  associatedMaterial?: Readonly<Item>;
  associatedProduct?: Readonly<Item>;
  associatedPurchaseOrder?: Readonly<PurchaseOrderHeader>;
  associatedStockTake?: Readonly<StockTakeHeader>;
  associatedSupplier?: Readonly<SupplierManufacturer>;
  associatedUllage?: Readonly<UllageHeader>;
  associatedVessel?: Readonly<Vessel>;
  associatedPallet?: Readonly<PalletHeader>;
  associatedStockTransfer?: Readonly<StockTransferHeader>;
  associatedStockAdjustment?: Readonly<StockAdjustmentHeader>;
  associatedBatch?: Readonly<BatchHeader>;
  associatedBatchProcessInstruction?: Readonly<ProcessInstructionWithParentDetails>;
}

export interface Associations<T = any> {
  getRoute: (item: T, task?: BmTask) => Location;
  getText: (item: T) => string; // For dropdowns
  getValue: (item: T) => Guid; // For dropdowns
  getAll: (opts: { initialItemId?: Guid | null }) => Promise<readonly T[]>;
}

export const availableEntityTypesFilter = (type: EnumBmEntityType) => {
  return (
    type.isAssembly() ||
    type.isAssemblyProcedure() ||
    type.isCredit() ||
    type.isLead() ||
    type.isMaterial() ||
    type.isOrder() ||
    type.isOutlet() ||
    type.isOutletGroup() ||
    type.isPriceList() ||
    type.isProduct() ||
    type.isPurchaseOrder() ||
    type.isStockTake() ||
    type.isSupplier() ||
    type.isUllage() ||
    type.isVessel() ||
    type.isPallet() ||
    type.isNone() ||
    type.isStockTransfer() ||
    type.isStockAdjustment() ||
    (hasFeatureUsingUserStore(EnumFeatureFlag.onlyDev) && type.isBatch()) ||
    (hasFeatureUsingUserStore(EnumFeatureFlag.onlyDev) &&
      type.isBatchProcessInstruction())
  );
};

const mapProcessLines = async (
  processLinesForAssemblies: readonly ProcessLineForAssembly[]
): Promise<ProcessLineWithAssembly[]> => {
  const processWithAssemblies: ProcessLineWithAssembly[] = [];

  const headerIds = processLinesForAssemblies.map((p) => p.assemblyId);
  const assemblyHeaders = await assemblyService
    .getAssemblyHeadersByIds(headerIds)
    .then((val) => new GuidMap(val.map((assembly) => [assembly.id, assembly])));

  processLinesForAssemblies.forEach((processLineForAssembly) => {
    let header = assemblyHeaders.get(processLineForAssembly.assemblyId);

    processWithAssemblies.push({
      processLine: processLineForAssembly.processLine,
      processLineId: processLineForAssembly.processLine.id,
      assemblyHeader: header!,
    });
  });

  return processWithAssemblies;
};

const mapProcessInstructions = async (
  instructions: readonly ProcessInstruction[]
): Promise<ProcessInstructionWithParentDetails[]> => {
  const processIds = distinctGuids(instructions.map((i) => i.processId));
  const processes = await batchService.getProcessHeaders(processIds);
  const processMap = toGuidMap(processes, (i) => i.id);

  const batchIds = distinctGuids(processes.map((p) => p.batchId));
  const batches = await batchService.getBatchHeaders(batchIds);
  const batchMap = toGuidMap(batches, (i) => i.id);

  return instructions
    .map((i): ProcessInstructionWithParentDetails | null => {
      const process = processMap.get(i.processId);

      if (!process) {
        return null;
      }

      return {
        instruction: i,
        process: process,
        batch: batchMap.get(process.batchId) ?? null,
      };
    })
    .filter(isNotNullOrUndefined);
};

export const getAssociationFromType = (
  type: EnumBmEntityType | null,
  cache: {
    outlets: OutletSummariesCache;
    supplierManufacturers: SupplierManufacturersCache;
    groups: GroupsCache;
    items: ItemsCache;
    priceLists: PriceListsCache;
    vessels: VesselsCache;
  }
): Associations | null => {
  const DAYS_IN_PAST = 28;

  switch (type) {
    case EnumBmEntityType.outlet:
      return {
        getRoute: (outlet, task) => {
          // If the task is for the current user then we will open outlet at CRM tab with "current task"
          const isForCurrentUser = !!task?.assignedToUserId?.equals(
            userStore.state.user?.id
          );
          return contactsMenu.viewOutlet(
            outlet.id,
            outlet.isRetail,
            isForCurrentUser ? task!.id : null
          );
        },
        getText: (outlet) => outlet.displayName + " | " + outlet.code,
        getValue: (outlet) => outlet.id,
        getAll: () => Promise.resolve(cache.outlets.all),
      } satisfies Associations<Readonly<OutletSummary>>;

    case EnumBmEntityType.order:
      return {
        getRoute: (order) => salesMenu.viewOrder(order.id),
        getText: (order) =>
          $m.enum_bm_entity_type_order + " " + order.orderNumber,
        getValue: (order) => order.id,
        getAll: async ({ initialItemId }) => {
          const orderFilter = new OrderFilter();
          orderFilter.fromCreatedAt = tenantService.now.minusSeconds(
            60 * 60 * 24 * DAYS_IN_PAST
          );

          const orderSummaries = (
            await orderService.getOrderSummaries(orderFilter)
          ).orders;

          const needsInitialItem =
            initialItemId &&
            !orderSummaries.some((x) => x.id.equals(initialItemId));

          const initialItemFilter = new OrderFilter();
          initialItemFilter.limitOrderHeaderIds = initialItemId
            ? [initialItemId]
            : [];

          return orderSummaries.concat(
            needsInitialItem
              ? (await orderService.getOrderSummaries(initialItemFilter)).orders
              : []
          );
        },
      } satisfies Associations<OrderSummary>;

    case EnumBmEntityType.credit:
      return {
        getRoute: (credit) => salesMenu.viewCreditNote(credit.id),
        getText: (credit) =>
          $m.enum_bm_entity_type_credit + " " + credit.creditNumber,
        getValue: (credit) => credit.id,
        getAll: async ({ initialItemId }) => {
          const creditFilter = new CreditFilter();
          creditFilter.fromCreatedAt = tenantService.now.minusSeconds(
            60 * 60 * 24 * DAYS_IN_PAST
          );

          const creditSummaries = (
            await creditService.getCreditSummaries(creditFilter)
          ).credits;

          const needsInitialItem =
            initialItemId &&
            !creditSummaries.some((x) => x.id.equals(initialItemId));

          const initialItemFilter = new CreditFilter();
          initialItemFilter.limitCreditIds = initialItemId
            ? [initialItemId]
            : [];

          return creditSummaries.concat(
            needsInitialItem
              ? (await creditService.getCreditSummaries(initialItemFilter))
                  .credits
              : []
          );
        },
      } satisfies Associations<CreditSummary>;

    case EnumBmEntityType.assembly:
      return {
        getRoute: (assembly) => {
          switch (assembly.assemblyType) {
            case EnumAssemblyType.productPackaging:
              return productionMenu.assembly.viewAssembly(assembly.id);
            case EnumAssemblyType.materialProduction:
              return productionMenu.batch.viewBatch(assembly.id);
            default:
              return productionMenu.assembly.viewAssembly(assembly.id);
          }
        },
        getText: (assembly) =>
          $m.enum_bm_entity_type_assembly + " " + assembly.name,
        getValue: (assembly) => assembly.id,
        getAll: async ({ initialItemId }) => {
          const assemblyFilter = new AssemblyFilter();
          assemblyFilter.fromPlannedStartDateInclusive =
            tenantService.today.minusDays(DAYS_IN_PAST);

          const assemblyHeaders = await assemblyService
            .getFilteredAssemblySummaries(assemblyFilter)
            .then((res) =>
              res.assemblySummaries.map((summary) => summary.header)
            );

          const needsInitialItem =
            initialItemId &&
            !assemblyHeaders.some((x) => x.id.equals(initialItemId));

          return assemblyHeaders.concat(
            needsInitialItem
              ? await assemblyService.getAssemblyHeadersByIds([initialItemId])
              : []
          );
        },
      } satisfies Associations<AssemblyHeader>;

    case EnumBmEntityType.assemblyProcedure:
      return {
        getRoute: (processWithAssembly) => {
          switch (processWithAssembly.assemblyHeader.assemblyType) {
            case EnumAssemblyType.productPackaging:
              return productionMenu.assembly.viewAssembly(
                processWithAssembly.assemblyHeader.id
              );
            case EnumAssemblyType.materialProduction:
              return productionMenu.batch.viewBatch(
                processWithAssembly.assemblyHeader.id
              );
            default:
              return productionMenu.assembly.viewAssembly(
                processWithAssembly.assemblyHeader.id
              );
          }
        },
        getText: (processWithAssembly) =>
          $m.enum_bm_entity_type_assembly +
          " " +
          processWithAssembly.assemblyHeader?.name +
          " | " +
          processWithAssembly.processLine?.instruction,
        getValue: (processWithAssembly) => processWithAssembly.processLineId,
        getAll: () =>
          assemblyService
            .getAllAssemblyProcessLines()
            .then((process) => mapProcessLines(process)),
      } satisfies Associations<ProcessLineWithAssembly>;

    case EnumBmEntityType.supplier:
      return {
        getRoute: (supplier) => contactsMenu.viewSupplier(supplier.id),
        getText: (supplier) => supplier.businessName,
        getValue: (supplier) => supplier.id,
        getAll: () => Promise.resolve(cache.supplierManufacturers.all),
      } satisfies Associations<Readonly<SupplierManufacturer>>;

    case EnumBmEntityType.outletGroup:
      return {
        getRoute: (outletGroup) => contactsMenu.viewOutletGroup(outletGroup.id),
        getText: (outletGroup) => outletGroup.name,
        getValue: (outletGroup) => outletGroup.id,
        getAll: () => Promise.resolve(cache.groups.all),
      } satisfies Associations<Readonly<Group>>;

    case EnumBmEntityType.purchaseOrder:
      return {
        getRoute: (po) => stockMenu.purchaseOrdering.viewPurchaseOrder(po.id),
        getText: (po) =>
          $m.enum_bm_entity_type_purchase_order + " " + po.readOnly?.number,
        getValue: (po) => po.id,
        getAll: async ({ initialItemId }) => {
          const purchaseOrderFilter = new PurchaseOrderFilter();
          purchaseOrderFilter.createdFrom =
            tenantService.today.minusDays(DAYS_IN_PAST);

          const purchaseOrderHeaders = await purchaseOrderService
            .getPurchaseOrderHeadersByFilter(purchaseOrderFilter)
            .then((res) => res.purchaseOrderHeaders);

          const needsInitialItem =
            initialItemId &&
            !purchaseOrderHeaders.some((x) => x.id.equals(initialItemId));

          const initialItemFilter = new PurchaseOrderFilter();
          initialItemFilter.purchaseOrderIds = initialItemId
            ? [initialItemId]
            : [];

          return purchaseOrderHeaders.concat(
            needsInitialItem
              ? (
                  await purchaseOrderService.getPurchaseOrderHeadersByFilter(
                    initialItemFilter
                  )
                ).purchaseOrderHeaders
              : []
          );
        },
      } satisfies Associations<PurchaseOrderHeader>;

    case EnumBmEntityType.product:
      return {
        getRoute: (item) => stockMenu.stock.viewLocation(item.details.id),
        getText: (item) => item.details.name,
        getValue: (item) => item.details.id,
        getAll: () => {
          return Promise.resolve(
            cache.items.all.filter((item) =>
              item.details.mainPurpose.isProduct()
            )
          );
        },
      } satisfies Associations<Readonly<Item>>;

    case EnumBmEntityType.material:
      return {
        getRoute: (item) => stockMenu.stock.viewLocation(item.details.id),
        getText: (item) => item.details.name,
        getValue: (item) => item.details.id,
        getAll: () => {
          return Promise.resolve(
            cache.items.all.filter((item) =>
              item.details.mainPurpose.isMaterial()
            )
          );
        },
      } satisfies Associations<Readonly<Item>>;

    case EnumBmEntityType.lead:
      return {
        getRoute: (lead) => salesMenu.leads.viewLead(lead.id),
        getText: (lead) => lead.businessName,
        getValue: (lead) => lead.id,
        getAll: async () => await leadService.getAllLeads(),
      } satisfies Associations<Lead>;

    case EnumBmEntityType.stockTake:
      return {
        getRoute: (stockTake) => stockMenu.stockTake.viewLocation(stockTake.id),
        getText: (stockTake) =>
          $m.enum_bm_entity_type_stock_take + " " + stockTake.readOnly.number,
        getValue: (stockTake) => stockTake.id,
        getAll: async ({ initialItemId }) => {
          const stockTakeFilter = new StockTakeFilter();
          stockTakeFilter.statuses = [
            EnumStockTakeStatus.open,
            EnumStockTakeStatus.inProgress,
            EnumStockTakeStatus.complete,
          ];

          const stockTakeHeaders = await stockTakeService
            .getStockTakeHeadersByFilter(stockTakeFilter)
            .then((res) => res.stockTakeHeaders);

          const needsInitialItem =
            initialItemId &&
            !stockTakeHeaders.some((x) => x.id.equals(initialItemId));

          return stockTakeHeaders.concat(
            needsInitialItem
              ? await stockTakeService.getStockTakeHeaders([initialItemId])
              : []
          );
        },
      } satisfies Associations<StockTakeHeader>;

    case EnumBmEntityType.ullage:
      return {
        getRoute: (ullage) => ullageRoutes.view.location(ullage.id),
        getText: (ullage) =>
          $m.enum_bm_entity_type_ullage + " " + ullage.readOnly.ullageNumber,
        getValue: (ullage) => ullage.id,
        getAll: async ({ initialItemId }) => {
          const ullageFilter = new UllageFilter();
          ullageFilter.fromCreatedDate = tenantService.instantToDate(
            tenantService.now.minusSeconds(60 * 60 * 24 * DAYS_IN_PAST)
          );

          // Map to header to avoid vuetify "header" bug
          const ullageHeaders = await ullageService
            .getUllagesByFilter(ullageFilter)
            .then((res) => res.ullages.map((ullage) => ullage.header));

          const needsInitialItem =
            initialItemId &&
            !ullageHeaders.some((x) => x.id.equals(initialItemId));

          return ullageHeaders.concat(
            needsInitialItem
              ? await ullageService.getUllageHeaders([initialItemId])
              : []
          );
        },
      } satisfies Associations<UllageHeader>;

    case EnumBmEntityType.priceList:
      return {
        getRoute: (priceList) =>
          priceListSubRoutes.pricesTab.location(priceList.id),
        getText: (priceList) =>
          $m.enum_bm_entity_type_price_list + " " + priceList.name,
        getValue: (priceList) => priceList.id,
        getAll: () => Promise.resolve(cache.priceLists.all),
      } satisfies Associations<Readonly<PriceList>>;

    case EnumBmEntityType.vessel:
      return {
        getRoute: (vessel) =>
          productionMenu.vessel.viewMaintenanceTab(vessel.id),
        getText: (vessel) => vessel.name,
        getValue: (vessel) => vessel.id,
        getAll: () => Promise.resolve(cache.vessels.all),
      } satisfies Associations<Readonly<Vessel>>;

    case EnumBmEntityType.pallet:
      return {
        getRoute: (pallet) => distributionMenu.pallets.viewLocation(pallet.id),
        getText: (pallet) =>
          translateWithParams("pallet_with_number", {
            number: pallet.readOnly.number,
          }),
        getValue: (pallet) => pallet.id,
        getAll: async ({ initialItemId }) => {
          const palletFilter = new PalletFilter();
          palletFilter.statuses = [EnumPalletStatus.in];

          const pallets = (await palletService.getPalletsByFilter(palletFilter))
            .pallets;

          const needsInitialItem =
            initialItemId &&
            !pallets.some((x) => x.header.id.equals(initialItemId));

          // Map to only headers to prevent bloat and vuetify "header" bug
          return pallets
            .concat(
              needsInitialItem
                ? await palletService.getPallets([initialItemId])
                : []
            )
            .map((x) => x.header);
        },
      } satisfies Associations<Readonly<PalletHeader>>;

    case EnumBmEntityType.stockTransfer:
      return {
        getRoute: (stockTransfer) =>
          stockMenu.stockMovements.stockTransfer.editStockTransfer(
            stockTransfer.id,
            false
          ),
        getText: (stockTransfer) =>
          $m.enum_bm_entity_type_stock_transfer +
          " " +
          stockTransfer.readOnly.number,
        getValue: (stockTransfer) => stockTransfer.id,
        getAll: async ({ initialItemId }) => {
          const stockTransferFilter = new StockTransferFilter();
          stockTransferFilter.fromDespatchDateInclusive =
            tenantService.today.minusDays(DAYS_IN_PAST);

          const stockTransferHeaders = await stockTransferService
            .getFilteredStockTransferHeaders(stockTransferFilter)
            .then((res) => res.stockTransferHeaders);

          const needsInitialItem =
            initialItemId &&
            !stockTransferHeaders.some((x) => x.id.equals(initialItemId));

          return stockTransferHeaders.concat(
            needsInitialItem
              ? await stockTransferService.getStockTransferHeaders([
                  initialItemId,
                ])
              : []
          );
        },
      } satisfies Associations<StockTransferHeader>;

    case EnumBmEntityType.stockAdjustment:
      return {
        getRoute: (stockAdjustment) =>
          stockMenu.stockMovements.stockAdjustment.viewStockAdjustment(
            stockAdjustment.id,
            false
          ),
        getText: (stockAdjustment) =>
          $m.enum_bm_entity_type_stock_adjustment +
          " " +
          stockAdjustment.readOnly.adjustmentNumber,
        getValue: (stockAdjustment) => stockAdjustment.id,
        getAll: async ({ initialItemId }) => {
          const stockAdjustmentFilter = new StockAdjustmentFilter();
          stockAdjustmentFilter.fromAdjustmentDateInclusive =
            tenantService.today.minusDays(DAYS_IN_PAST);

          const stockAdjustments = await stockAdjustmentService
            .getFilteredStockAdjustmentHeaders(stockAdjustmentFilter)
            .then((res) => res.stockAdjustments);

          const needsInitialItem =
            initialItemId &&
            !stockAdjustments.some((x) => x.id.equals(initialItemId));

          const initialItemFilter = new StockAdjustmentFilter();
          initialItemFilter.limitStockAdjustmentIds = initialItemId
            ? [initialItemId]
            : [];

          return stockAdjustments.concat(
            needsInitialItem
              ? (
                  await stockAdjustmentService.getFilteredStockAdjustmentHeaders(
                    initialItemFilter
                  )
                ).stockAdjustments
              : []
          );
        },
      } satisfies Associations<StockAdjustmentHeader>;

    case EnumBmEntityType.batch:
      return {
        getRoute: (batch) => batchMenu.batch.viewBatch(batch.id),
        getText: (batch) => $m.enum_bm_entity_type_batch + " " + batch.urn,
        getValue: (batch) => batch.id,
        getAll: async ({ initialItemId }) => {
          const batchFilter = new BatchFilter();
          batchFilter.fromStartedAtInclusive = tenantService.now.minusSeconds(
            60 * 60 * 24 * DAYS_IN_PAST
          );

          const batchHeaders = (
            await batchService.getBatchHeadersByFilter(batchFilter)
          ).batchHeaders;

          const needsInitialItem =
            initialItemId &&
            !batchHeaders.some((x) => x.id.equals(initialItemId));

          return batchHeaders.concat(
            needsInitialItem
              ? await batchService.getBatchHeaders([initialItemId])
              : []
          );
        },
      } satisfies Associations<BatchHeader>;

    case EnumBmEntityType.batchProcessInstruction:
      return {
        getRoute: (processInstruction) =>
          batchMenu.batch.viewProcess(processInstruction.process.id),
        getText: (processInstruction) =>
          batchProcessInstructionText(processInstruction),
        getValue: (processInstruction) => processInstruction.instruction.id,
        getAll: async ({ initialItemId }) => {
          const filter = new ProcessInstructionFilter();
          filter.processStatuses = [
            EnumAssemblyStatus.open,
            EnumAssemblyStatus.inProgress,
          ];

          let instructions = (
            await batchService.getFilteredProcessInstructions(filter)
          ).processInstructions;

          if (
            initialItemId &&
            instructions.every((x) => !x.id.equals(initialItemId))
          ) {
            instructions = instructions.concat(
              await batchService.getProcessInstructions([initialItemId])
            );
          }

          return mapProcessInstructions(instructions);
        },
      } satisfies Associations<ProcessInstructionWithParentDetails>;

    default:
      return null;
  }
};

const batchProcessInstructionText = (
  processInstruction: ProcessInstructionWithParentDetails
) => {
  if (processInstruction.batch) {
    return translateWithParams("batch_process_instruction_with_batch", {
      batchName: processInstruction.batch.urn,
      processName: processInstruction.process.name,
      instruction: processInstruction.instruction.instruction,
    });
  }

  return translateWithParams("batch_process_instruction_no_batch", {
    processName: processInstruction.process.name,
    instruction: processInstruction.instruction.instruction,
  });
};

const fetchTaskAssociationDependencies = async (tasks: readonly BmTask[]) => {
  const outletIds = new GuidSet();
  let outletLastOrderTimestamp = new GuidMap<OutletLastOrderTimestamp>();

  const orderIds = new GuidSet();
  let orders = new GuidMap<OrderSummary>();

  const creditIds = new GuidSet();
  let credits = new GuidMap<CreditSummary>();

  const assemblyIds = new GuidSet();
  let assemblies = new GuidMap<AssemblyHeader>();

  const assemblyProcessIds = new GuidSet();
  let assemblyProcessLines = new GuidMap<ProcessLineWithAssembly>();

  const outletGroupIds = new GuidSet();
  let outletGroups = new GuidMap<Readonly<Group>>();

  const purchaseOrderIds = new GuidSet();
  let purchaseOrders = new GuidMap<PurchaseOrderHeader>();

  const stockItemIds = new GuidSet();
  let products = new GuidMap<Readonly<Item>>();
  let materials = new GuidMap<Readonly<Item>>();

  const leadIds = new GuidSet();
  let leads = new GuidMap<Lead>();

  const stockTakeIds = new GuidSet();
  let stockTakes = new GuidMap<StockTakeHeader>();

  const ullageIds = new GuidSet();
  let ullages = new GuidMap<UllageHeader>();

  const palletIds = new GuidSet();
  let palletHeaders = new GuidMap<PalletHeader>();

  const stockTransferIds = new GuidSet();
  let stockTransfers = new GuidMap<StockTransferHeader>();

  const stockAdjustmentIds = new GuidSet();
  let stockAdjustments = new GuidMap<StockAdjustmentHeader>();

  const batchIds = new GuidSet();
  let batches = new GuidMap<BatchHeader>();

  const processInstructionIds = new GuidSet();
  let processInstructions = new GuidMap<ProcessInstructionWithParentDetails>();

  const cache = apiCache.buildCacheSet({
    items: ItemsCache,
    groups: GroupsCache,
  });

  await cache.update();

  // Separate out all ids to the relevant sets
  tasks.forEach((task) => {
    if (!task.associatedBmEntityType || !task.associatedBmEntityId) {
      return;
    }

    if (task.associatedBmEntityType.isOutlet()) {
      outletIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isOrder()) {
      orderIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isCredit()) {
      creditIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isAssembly()) {
      assemblyIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isAssemblyProcedure()) {
      assemblyProcessIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isOutletGroup()) {
      outletGroupIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isPurchaseOrder()) {
      purchaseOrderIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isProduct()) {
      stockItemIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isMaterial()) {
      stockItemIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isLead()) {
      leadIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isStockTake()) {
      stockTakeIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isUllage()) {
      ullageIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isPallet()) {
      palletIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isStockTransfer()) {
      stockTransferIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isStockAdjustment()) {
      stockAdjustmentIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isBatch()) {
      batchIds.add(task.associatedBmEntityId);
    } else if (task.associatedBmEntityType.isBatchProcessInstruction()) {
      processInstructionIds.add(task.associatedBmEntityId);
    }
  });

  const promises: Promise<any>[] = [];

  // Only if we have ids for type, push our promise to the array
  if (outletIds.size) {
    promises.push(
      orderService
        .getLastOrderTimestamps(Array.from(outletIds))
        .then(
          (val) =>
            (outletLastOrderTimestamp = toGuidMap(
              val,
              (outletTimestamp) => outletTimestamp.outletId
            ))
        )
    );
  }

  if (orderIds.size) {
    const orderFilter = new OrderFilter();
    orderFilter.limitOrderHeaderIds = Array.from(orderIds);

    promises.push(
      orderService
        .getOrderSummaries(orderFilter)
        .then(
          (val) =>
            (orders = new GuidMap(val.orders.map((order) => [order.id, order])))
        )
    );
  }
  if (creditIds.size) {
    const creditFilter = new CreditFilter();
    creditFilter.limitCreditIds = Array.from(creditIds);

    promises.push(
      creditService
        .getCreditSummaries(creditFilter)
        .then(
          (val) =>
            (credits = new GuidMap(
              val.credits.map((credit) => [credit.id, credit])
            ))
        )
    );
  }
  if (assemblyIds.size) {
    promises.push(
      assemblyService
        .getAssemblyHeadersByIds(Array.from(assemblyIds))
        .then(
          (val) =>
            (assemblies = new GuidMap(
              val.map((assembly) => [assembly.id, assembly])
            ))
        )
    );
  }
  if (assemblyProcessIds.size) {
    promises.push(
      assemblyService
        .getAssemblyProcessLines(Array.from(assemblyProcessIds))
        .then(
          async (val) =>
            (assemblyProcessLines = new GuidMap(
              (await mapProcessLines(val)).map((x) => [x.processLineId, x])
            ))
        )
    );
  }
  if (outletGroupIds.size) {
    outletGroups = toGuidMap(cache.groups.all, (group) => group.id);
  }
  if (purchaseOrderIds.size) {
    promises.push(
      purchaseOrderService
        .getPurchaseOrders(Array.from(purchaseOrderIds))
        .then(
          (val) =>
            (purchaseOrders = new GuidMap(
              val.map((po) => [po.header.id, po.header])
            ))
        )
    );
  }
  if (stockItemIds.size) {
    products = toGuidMap(
      cache.items.all.filter((item) => item.details.mainPurpose.isProduct()),
      (item) => item.details.id
    );

    materials = toGuidMap(
      cache.items.all.filter((item) => item.details.mainPurpose.isMaterial()),
      (item) => item.details.id
    );
  }
  if (leadIds.size) {
    promises.push(
      leadService
        .getLeads(Array.from(leadIds))
        .then((val) => (leads = new GuidMap(val.map((ld) => [ld.id, ld]))))
    );
  }
  if (stockTakeIds.size) {
    promises.push(
      stockTakeService
        .getStockTakeHeaders(Array.from(stockTakeIds))
        .then((val) => (stockTakes = new GuidMap(val.map((st) => [st.id, st]))))
    );
  }
  if (ullageIds.size) {
    promises.push(
      ullageService
        .getUllageHeaders(Array.from(ullageIds))
        .then((val) => (ullages = new GuidMap(val.map((u) => [u.id, u]))))
    );
  }
  if (palletIds.size) {
    promises.push(
      palletService.getPallets(Array.from(palletIds)).then(
        (val) =>
          (palletHeaders = toGuidMap(
            // Only store headers to prevent bloat, and Vuetify "header" property bug
            val.map((v) => v.header),
            (p) => p.id
          ))
      )
    );
  }
  if (stockTransferIds.size) {
    promises.push(
      stockTransferService
        .getStockTransferHeaders(Array.from(stockTransferIds))
        .then((val) => (stockTransfers = toGuidMap(val, (st) => st.id)))
    );
  }
  if (stockAdjustmentIds.size) {
    promises.push(
      stockAdjustmentService
        .getStockAdjustments(Array.from(stockAdjustmentIds))
        .then(
          (val) =>
            (stockAdjustments = toGuidMap(
              val.map((v) => v.header),
              (sa) => sa.id
            ))
        )
    );
  }
  if (batchIds.size) {
    promises.push(
      batchService
        .getBatchHeaders(Array.from(batchIds))
        .then(
          (val) =>
            (batches = new GuidMap(val.map((batch) => [batch.id, batch])))
        )
    );
  }
  if (processInstructionIds.size) {
    promises.push(
      batchService
        .getProcessInstructions(Array.from(processInstructionIds))
        .then(
          async (val) =>
            (processInstructions = new GuidMap(
              (await mapProcessInstructions(val)).map((x) => [
                x.instruction.id,
                x,
              ])
            ))
        )
    );
  }

  // Get all dependencies together
  await Promise.all(promises);

  return {
    orders,
    credits,
    assemblies,
    assemblyProcessLines,
    outletGroups,
    purchaseOrders,
    products,
    materials,
    leads,
    stockTakes,
    ullages,
    outletLastOrderTimestamp,
    palletHeaders,
    stockTransfers,
    stockAdjustments,
    batches,
    processInstructions,
  };
};

export const joinTasksWithAssociations = async (
  tasks: readonly BmTask[]
): Promise<TaskWithAssociations[]> => {
  const cache = buildCache()
    .withOutletSummaries()
    .withSupplierManufacturers()
    .withPriceLists()
    .withVessels()
    .withGroups()
    .withItems()
    .build();

  const [dependencies] = await Promise.all([
    fetchTaskAssociationDependencies(tasks),
    cache.update(),
  ]);
  return tasks.map((task): TaskWithAssociations => {
    const associations = getAssociationFromType(
      task.associatedBmEntityType,
      cache
    );

    // If we don't have a relevant association
    if (
      !task.associatedBmEntityId ||
      !task.associatedBmEntityType ||
      !associations
    ) {
      return {
        task,
        associationName: null,
        associationRoute: null,
      };
    }

    const { getText, getRoute } = associations;

    switch (task.associatedBmEntityType) {
      case EnumBmEntityType.outlet: {
        const outletId = task.associatedBmEntityId;
        const associatedOutlet = cache.outlets.getById(outletId) ?? undefined;

        const associatedOutletLastOrder =
          dependencies.outletLastOrderTimestamp.get(outletId);
        return {
          task,
          associationName: getText(associatedOutlet),
          associationRoute: getRoute(associatedOutlet, task),
          associatedOutlet,
          associatedOutletLastOrder,
        };
      }

      case EnumBmEntityType.order: {
        const associatedOrder = dependencies.orders.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedOrder),
          associationRoute: getRoute(associatedOrder),
          associatedOrder,
        };
      }

      case EnumBmEntityType.credit: {
        const associatedCredit = dependencies.credits.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedCredit),
          associationRoute: getRoute(associatedCredit),
          associatedCredit,
        };
      }

      case EnumBmEntityType.assembly: {
        const associatedAssembly = dependencies.assemblies.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedAssembly),
          associationRoute: getRoute(associatedAssembly),
          associatedAssembly,
        };
      }

      case EnumBmEntityType.assemblyProcedure: {
        const associatedAssemblyProcessLine =
          dependencies.assemblyProcessLines.get(task.associatedBmEntityId);
        return {
          task,
          associationName: getText(associatedAssemblyProcessLine),
          associationRoute: getRoute(associatedAssemblyProcessLine),
          associatedAssemblyProcessLine,
        };
      }

      case EnumBmEntityType.supplier: {
        const associatedSupplierManufacturer =
          cache.supplierManufacturers.getById(task.associatedBmEntityId) ??
          undefined;
        return {
          task,
          associationName: getText(associatedSupplierManufacturer),
          associationRoute: getRoute(associatedSupplierManufacturer),
          associatedSupplier: associatedSupplierManufacturer,
        };
      }

      case EnumBmEntityType.outletGroup: {
        const associatedOutletGroup = dependencies.outletGroups.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedOutletGroup),
          associationRoute: getRoute(associatedOutletGroup),
          associatedOutletGroup,
        };
      }

      case EnumBmEntityType.purchaseOrder: {
        const associatedPurchaseOrder = dependencies.purchaseOrders.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedPurchaseOrder),
          associationRoute: getRoute(associatedPurchaseOrder),
          associatedPurchaseOrder,
        };
      }

      case EnumBmEntityType.product: {
        const associatedProduct = dependencies.products.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedProduct),
          associationRoute: getRoute(associatedProduct),
          associatedProduct,
        };
      }

      case EnumBmEntityType.material: {
        const associatedMaterial = dependencies.materials.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedMaterial),
          associationRoute: getRoute(associatedMaterial),
          associatedMaterial,
        };
      }

      case EnumBmEntityType.lead: {
        const associatedLead = dependencies.leads.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedLead),
          associationRoute: getRoute(associatedLead),
          associatedLead,
        };
      }

      case EnumBmEntityType.stockTake: {
        const associatedStockTake = dependencies.stockTakes.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedStockTake),
          associationRoute: getRoute(associatedStockTake),
          associatedStockTake,
        };
      }

      case EnumBmEntityType.ullage: {
        const associatedUllage = dependencies.ullages.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedUllage),
          associationRoute: getRoute(associatedUllage),
          associatedUllage,
        };
      }

      case EnumBmEntityType.priceList: {
        const associatedPriceList =
          cache.priceLists.getById(task.associatedBmEntityId) ?? undefined;
        return {
          task,
          associationName: getText(associatedPriceList),
          associationRoute: getRoute(associatedPriceList),
          associatedPriceList,
        };
      }

      case EnumBmEntityType.vessel: {
        const associatedVessel =
          cache.vessels.getById(task.associatedBmEntityId) ?? undefined;
        return {
          task,
          associationName: getText(associatedVessel),
          associationRoute: getRoute(associatedVessel),
          associatedVessel,
        };
      }

      case EnumBmEntityType.pallet: {
        const associatedPalletHeader = dependencies.palletHeaders.get(
          task.associatedBmEntityId
        );

        return {
          task,
          associationName: getText(associatedPalletHeader),
          associationRoute: getRoute(associatedPalletHeader),
          associatedPallet: associatedPalletHeader,
        };
      }

      case EnumBmEntityType.stockTransfer: {
        const associatedStockTransfer = dependencies.stockTransfers.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedStockTransfer),
          associationRoute: getRoute(associatedStockTransfer),
          associatedStockTransfer,
        };
      }

      case EnumBmEntityType.stockAdjustment: {
        const associatedStockAdjustment = dependencies.stockAdjustments.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedStockAdjustment),
          associationRoute: getRoute(associatedStockAdjustment),
          associatedStockAdjustment,
        };
      }

      case EnumBmEntityType.batch: {
        const associatedBatch = dependencies.batches.get(
          task.associatedBmEntityId
        );
        return {
          task,
          associationName: getText(associatedBatch),
          associationRoute: getRoute(associatedBatch),
          associatedBatch,
        };
      }

      case EnumBmEntityType.batchProcessInstruction: {
        const associatedProcessInstruction =
          dependencies.processInstructions.get(task.associatedBmEntityId);
        return {
          task,
          associationName: getText(associatedProcessInstruction),
          associationRoute: getRoute(associatedProcessInstruction),
          associatedBatchProcessInstruction: associatedProcessInstruction,
        };
      }

      default:
        return {
          task,
          associationName: null,
          associationRoute: null,
        };
    }
  });
};
